export const ISO42001_CANONICAL_CODE = "ISO42001";
export const ISO42001_QUICK_RECORD_CODE = "ISO_42001";
export const ISO42001_CORPUS_VERSION = "iso42001-2023.1";

const ALIASES = new Set([
  "ISO42001",
  "ISO_42001",
  "ISO42001_2023",
  "ISO/IEC 42001",
  "ISO/IEC 42001:2023",
  "ISO 42001",
]);

export type Iso42001UiState = "loading" | "error" | "empty" | "ready_empty" | "ready";

export function isIso42001Selection(code: string | null | undefined): boolean {
  return ALIASES.has(String(code || "").trim().toUpperCase());
}

export function iso42001UiState(input: {
  loading: boolean;
  error: boolean;
  definitionAvailable: boolean;
  answered: number;
}): Iso42001UiState {
  if (input.loading) return "loading";
  if (input.error) return "error";
  if (!input.definitionAvailable) return "empty";
  if (!input.answered) return "ready_empty";
  return "ready";
}

export function iso42001FullProgress(
  questionIds: string[],
  answers: Record<string, string | undefined> | null | undefined,
) {
  const keys = new Set(questionIds);
  let answered = 0;
  for (const [key, value] of Object.entries(answers || {})) {
    if (keys.has(key) && value) answered += 1;
  }
  const total = questionIds.length;
  return { answered, total, percent: total > 0 ? Math.round((answered / total) * 100) : 0 };
}

export function recommendationsFromIso42001Blocks(
  blocks: Array<{
    questions?: Array<{
      id: string;
      questionKey?: string;
      domainKey?: string;
      controlCode?: string | null;
      recommendationHint?: { en?: string; ru?: string; es?: string };
    }>;
  }>,
  answers: Record<string, string | undefined> | null | undefined,
  locale: "en" | "ru" | "es" = "en",
) {
  const out: Array<{
    id: string;
    questionKey: string;
    domainKey: string;
    controlCode: string | null;
    standardCode: "ISO42001";
    priority: "HIGH" | "MEDIUM";
    title: string;
  }> = [];
  for (const block of blocks || []) {
    for (const question of block.questions || []) {
      const answer = answers?.[question.id];
      if (answer !== "no" && answer !== "partial") continue;
      const hint = question.recommendationHint?.[locale] || question.recommendationHint?.en || "";
      if (!hint) continue;
      out.push({
        id: `ISO42001:${question.questionKey || question.id}`,
        questionKey: question.questionKey || question.id,
        domainKey: question.domainKey || "",
        controlCode: question.controlCode || null,
        standardCode: "ISO42001",
        priority: answer === "no" ? "HIGH" : "MEDIUM",
        title: hint,
      });
    }
  }
  return out;
}

export function groupQuickAnswersByCategory(
  categories: Array<{ id: string; questions?: Array<{ id: string }> }>,
  flatAnswers: Record<string, number> | null | undefined,
) {
  const grouped: Record<string, Record<string, number>> = {};
  for (const category of categories || []) {
    const bucket: Record<string, number> = {};
    for (const question of category.questions || []) {
      const value = flatAnswers?.[question.id];
      if (value == null) continue;
      bucket[question.id] = Number(value);
    }
    if (Object.keys(bucket).length > 0) grouped[category.id] = bucket;
  }
  return grouped;
}
